"use client";

import { useEffect, useMemo, useState } from "react";
import { offersData, type Offer } from "@/data/offers";
import Image from "next/image";
import { Title } from "@/components/common/typography/Title";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { MdiChevronLeft } from "@/assets/icons/mdi-chevron-left";
import { MdiChevronRight } from "@/assets/icons/mdi-chevron-right";
import {
  refreshContactAnchorIfNeeded,
  saveContactIntent,
} from "@/lib/contact-intent";

const cards: Offer[] = [
  offersData.sharedOpenSpace,
  offersData.privateOffices,
  offersData.privateOpenSpace,
  offersData.entireFloor,
  offersData.meetingRooms,
];

function OfferImageGrid({
  offer,
  onOpen,
}: {
  offer: Offer;
  onOpen: (offer: Offer, index: number) => void;
}) {
  if (offer.photos.length === 1) {
    return (
      <button
        type="button"
        onClick={() => onOpen(offer, 0)}
        className="relative h-[240px] w-full overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-[280px]"
        aria-label={`Ouvrir la galerie ${offer.name}`}
      >
        <Image
          src={offer.photos[0]}
          alt={offer.name}
          fill
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="object-cover transition-transform duration-300 hover:scale-[1.02]"
        />
      </button>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
      {offer.photos.map((photo, index) => (
        <button
          type="button"
          onClick={() => onOpen(offer, index)}
          key={`${offer.id}-${index}`}
          className={`relative aspect-[4/3] overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            offer.photos.length % 2 !== 0 && index === offer.photos.length - 1
              ? "col-span-2 sm:col-span-1"
              : ""
          }`}
          aria-label={`Ouvrir la photo ${index + 1} de ${offer.name}`}
        >
          <Image
            src={photo}
            alt={`${offer.name} ${index + 1}`}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1023px) 33vw, 16vw"
            className="object-cover transition-transform duration-300 hover:scale-[1.03]"
          />
        </button>
      ))}
    </div>
  );
}

export const OffersSection = () => {
  const [viewerOffer, setViewerOffer] = useState<Offer | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const viewerPhotos = useMemo(
    () => (viewerOffer ? viewerOffer.photos : []),
    [viewerOffer],
  );

  const openViewer = (offer: Offer, index: number) => {
    setViewerOffer(offer);
    setActiveIndex(index);
  };

  const closeViewer = () => {
    setViewerOffer(null);
    setActiveIndex(0);
  };

  const goTo = (index: number) => {
    if (!viewerPhotos.length) return;
    const normalized = (index + viewerPhotos.length) % viewerPhotos.length;
    setActiveIndex(normalized);
  };

  const goPrevious = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  useEffect(() => {
    if (!viewerOffer) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeViewer();
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [viewerOffer, activeIndex, viewerPhotos.length]);

  return (
    <>
      <section
        className="flex flex-col gap-12 px-4 pb-28 pt-16 sm:px-6 sm:pb-20 sm:pt-20 md:px-10 lg:px-16 xl:px-24 2xl:px-32"
        id="offers"
      >
        <Title title="Nos offres" />

        <div className="grid grid-cols-1 gap-14 text-black lg:grid-cols-2 lg:gap-12">
          {cards.map((offer) => (
            <article key={offer.id} className="flex h-full flex-col gap-4">
              <OfferImageGrid offer={offer} onOpen={openViewer} />

              <h2 className="text-2xl font-semibold">{offer.name}</h2>
              <p className="text-sm font-medium text-gray-600">
                {offer.capacity}
              </p>
              <p className="text-red-500">
                {offer.priceLabel ?? `${offer.prix}EUR/mois`}
              </p>
              <p className="flex-grow">{offer.shortDescription}</p>

              <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-row sm:flex-wrap">
                {offer.discoverable && (
                  <PrimaryButton
                    variant="black"
                    text="Découvrir l'offre"
                    navigateTo={`/offres/${offer.id}`}
                    className="w-full text-center sm:w-auto"
                  />
                )}
                <PrimaryButton
                  text="Nous contacter"
                  navigateTo="#contact"
                  onClick={() => {
                    saveContactIntent({
                      offerType: offer.contactOfferType,
                      source: `lp-offres-${offer.id}`,
                    });
                    refreshContactAnchorIfNeeded();
                  }}
                  className="w-full text-center sm:w-auto"
                />
              </div>
            </article>
          ))}
        </div>
      </section>

      {viewerOffer && viewerPhotos.length > 0 && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-3 sm:p-6"
          onClick={closeViewer}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Galerie ${viewerOffer.name}`}
            className="relative w-full max-w-6xl rounded-xl bg-black/95 p-3 text-white sm:p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeViewer}
              aria-label="Fermer la galerie"
              className="absolute right-3 top-3 z-10 rounded-full border border-white/40 px-3 py-1 text-sm hover:bg-white/10"
            >
              Fermer
            </button>

            <div className="mb-3 flex items-center justify-between gap-3 pr-20">
              <p className="truncate text-sm sm:text-base">
                {viewerOffer.name}
              </p>
              <p className="text-xs text-white/80 sm:text-sm">
                {activeIndex + 1} / {viewerPhotos.length}
              </p>
            </div>

            <div className="relative h-[52vh] w-full overflow-hidden rounded-lg sm:h-[68vh]">
              <Image
                src={viewerPhotos[activeIndex]}
                alt={`${viewerOffer.name} ${activeIndex + 1}`}
                fill
                sizes="100vw"
                className="object-contain"
                priority
              />
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={goPrevious}
                aria-label="Image précédente"
                className="flex items-center gap-1 rounded-lg border border-white/30 px-3 py-2 text-sm hover:bg-white/10"
              >
                <MdiChevronLeft className="h-5 w-5" />
                Précédente
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Image suivante"
                className="flex items-center gap-1 rounded-lg border border-white/30 px-3 py-2 text-sm hover:bg-white/10"
              >
                Suivante
                <MdiChevronRight className="h-5 w-5" />
              </button>
            </div>

            {viewerPhotos.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {viewerPhotos.map((photo, index) => (
                  <button
                    type="button"
                    key={`${viewerOffer.id}-thumb-${index}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Voir image ${index + 1}`}
                    className={`relative h-14 w-20 flex-none overflow-hidden rounded-md border ${
                      activeIndex === index
                        ? "border-white"
                        : "border-white/20 opacity-80"
                    }`}
                  >
                    <Image
                      src={photo}
                      alt={`${viewerOffer.name} miniature ${index + 1}`}
                      fill
                      sizes="80px"
                      className="object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
